/**
 * Explains the NBU standard itself, with the field tables rendered from the
 * live backend spec so this page can never disagree with the encoder.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { Alert, Badge, Card, Spinner, Tabs, cx } from '../components/ui'
import { request } from '../lib/api'
import type { QRVersion, VersionSpec } from '../lib/types'

export function Standard() {
  const [versions, setVersions] = useState<VersionSpec[] | null>(null)
  const [active, setActive] = useState<QRVersion>('v2')

  useEffect(() => {
    request<{ versions: VersionSpec[] }>('/meta/spec', { auth: false })
      .then((r) => setVersions(r.versions))
      .catch(() => setVersions([]))
  }, [])

  const spec = versions?.find((v) => v.version === active)

  return (
    <div className="container-page max-w-4xl py-12">
      <header>
        <Badge tone="brand" className="mb-4">
          Довідка
        </Badge>
        <h1 className="text-4xl font-bold tracking-tight">
          Стандарт QR-кодів НБУ для кредитових переказів
        </h1>
        <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
          Платіжний QR-код — це не посилання і не картинка з даними. Це звичайний текст із
          фіксованим порядком рядків, який банківський застосунок читає і підставляє у
          платіжну інструкцію.
        </p>
      </header>

      <section className="mt-12">
        <h2 className="text-2xl font-bold tracking-tight">Як влаштований payload</h2>
        <p className="mt-3 text-slate-600 dark:text-slate-300">
          Кожен рядок — окремий реквізит, порядок фіксований, порожній рядок означає
          «поле не заповнене». Кінцеві порожні рядки можна не передавати — саме це утримує
          реальні платежі в межах ліміту довжини.
        </p>
        <Alert tone="warning" title="Два місця, де найчастіше помиляються">
          <p>
            Рядок 5 — <b>зарезервований</b>, а не BIC банку: стандарт українських переказів
            не містить ідентифікатора банку, бо МФО вже вшите в IBAN.
          </p>
          <p className="mt-2">
            Рядок 9 — <b>код отримувача (ЄДРПОУ або РНОКПП)</b>, і він{' '}
            <b>обовʼязковий у всіх версіях</b>. Це головна відмінність від європейського
            EPC-коду, на який формат схожий зовні.
          </p>
        </Alert>

        <div className="mt-5 overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
          <pre className="overflow-x-auto p-4 font-mono text-[13px] leading-relaxed text-slate-100">
            {[
              ['BCD', 'службова мітка — завжди BCD'],
              ['002', 'версія формату: 001 / 002 / 003'],
              ['1', 'кодування: 1 = UTF-8, 2 = Windows-1251'],
              ['UCT', 'функція — Ukrainian Credit Transfer'],
              ['', 'зарезервовано стандартом'],
              ['ТОВ "Ромашка"', 'найменування отримувача'],
              ['UA2132231300000260072335…', 'IBAN отримувача'],
              ['UAH1250.00', 'сума'],
              ['32129530', 'код отримувача — ЄДРПОУ / РНОКПП'],
              ['', 'категорія платежу (з v3)'],
              ['', 'референс (з v2)'],
              ['Оплата за рахунком №12/8', 'призначення платежу'],
              ['Дякуємо за оплату!', 'інформація для платника'],
            ]
              .map(
                ([value, comment], i) =>
                  `${String(i + 1).padStart(2, ' ')} │ ${value.padEnd(26)} ← ${comment}`,
              )
              .join('\n')}
          </pre>
        </div>
      </section>

      <section className="mt-12">
        <h2 className="text-2xl font-bold tracking-tight">Чим відрізняються версії</h2>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          {[
            {
              v: 'v1',
              title: 'Формат 001',
              body: 'Первинна версія (постанова НБУ № 68 від 28.05.2020). Референс і категорія платежу ще не використовуються.',
              tone: 'neutral' as const,
            },
            {
              v: 'v2',
              title: 'Формат 002',
              body: 'Найпоширеніша версія — її читають усі українські банківські застосунки. Додано референс для звірки рахунків.',
              tone: 'brand' as const,
            },
            {
              v: 'v3',
              title: 'Формат 003',
              body: 'Чинна з 01.11.2025 (постанова № 97). Миттєві перекази ICT/XCT, заборона зміни полів, строк дії, призначення до 420 символів.',
              tone: 'neutral' as const,
            },
          ].map((item) => (
            <Card key={item.v} className={cx(item.tone === 'brand' && 'ring-2 ring-brand-500')}>
              <Badge tone={item.tone}>{item.v.toUpperCase()}</Badge>
              <h3 className="mt-3 font-semibold">{item.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400">{item.body}</p>
            </Card>
          ))}
        </div>

        <Alert tone="brand" title="Яку версію обрати?">
          Беріть <b>v2</b>: її гарантовано читають усі банківські застосунки.{' '}
          <b>v3</b> потрібна, коли треба миттєвий переказ, строк дії рахунку або заборона
          платнику редагувати суму чи реквізити — але спершу переконайтеся, що банки ваших
          клієнтів її вже підтримують.
        </Alert>
      </section>

      <section className="mt-12">
        <h2 className="text-2xl font-bold tracking-tight">Повний перелік полів</h2>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Таблиця будується з того самого опису, який використовує наш валідатор — вона
          не може розійтися з поведінкою API.
        </p>

        <div className="mt-5">
          <Tabs<QRVersion>
            value={active}
            onChange={setActive}
            tabs={[
              { value: 'v1', label: 'v1' },
              { value: 'v2', label: 'v2' },
              { value: 'v3', label: 'v3' },
            ]}
          />
        </div>

        {!versions ? (
          <div className="grid place-items-center py-16">
            <Spinner className="h-7 w-7 text-brand-600" />
          </div>
        ) : spec ? (
          <>
            <p className="mt-5 text-sm text-slate-600 dark:text-slate-300">{spec.summary}</p>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Максимальна довжина payload — {spec.maxPayloadBytes} байт (символ QR
              версії {spec.maxQrVersion}). Рекомендована корекція помилок —{' '}
              {spec.recommendedEcc}.
            </p>

            <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                  <tr>
                    <th className="px-3 py-2.5 font-medium">#</th>
                    <th className="px-3 py-2.5 font-medium">Поле</th>
                    <th className="px-3 py-2.5 font-medium">Назва</th>
                    <th className="px-3 py-2.5 font-medium">Макс.</th>
                    <th className="px-3 py-2.5 font-medium">Обовʼязкове</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {spec.fields.map((field) => (
                    <tr key={field.name} className={field.system ? 'opacity-60' : undefined}>
                      <td className="px-3 py-2.5 tabular-nums text-slate-400">{field.line}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-brand-700 dark:text-brand-300">
                        {field.name}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium">{field.title.uk}</div>
                        {field.hint && (
                          <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            {field.hint}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-slate-500">
                        {field.maxLength ?? '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        {field.system ? (
                          <Badge>службове</Badge>
                        ) : field.required ? (
                          <Badge tone="danger">так</Badge>
                        ) : (
                          <span className="text-slate-400">ні</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </section>

      <section className="mt-12">
        <h2 className="text-2xl font-bold tracking-tight">Часті помилки інтеграції</h2>
        <div className="mt-5 space-y-3">
          {[
            {
              t: 'Порожній код отримувача',
              b: 'Рядок 9 (ЄДРПОУ або РНОКПП) обовʼязковий у всіх версіях. Найчастіша помилка тих, хто портує генератор з європейського EPC-коду, де такого поля немає.',
            },
            {
              t: 'BIC у пʼятому рядку',
              b: 'Рядок 5 зарезервований і має бути порожнім. Якщо записати туди BIC, застосунок банку зчитає реквізити зі зсувом на одне поле.',
            },
            {
              t: 'Перевищення довжини payload',
              b: 'Ліміт рахується в байтах, а кирилична літера в UTF-8 займає два. Формат 003 дозволяє 420 символів призначення, але весь payload обмежений 507 байтами.',
            },
            {
              t: 'Логотип без підвищеної корекції',
              b: 'Накладання логотипа знищує частину модулів. Без ecc=H код зчитається на екрані, але не з друку під кутом.',
            },
            {
              t: 'Сума з трьома знаками після коми',
              b: 'Стандарт допускає рівно два. Округлюйте на своєму боці — інакше отримаєте 422 ще до генерації.',
            },
          ].map((item) => (
            <Card key={item.t}>
              <h3 className="font-semibold">{item.t}</h3>
              <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400">{item.b}</p>
            </Card>
          ))}
        </div>
      </section>

      <div className="mt-12 flex flex-wrap gap-3">
        <Link to="/generator" className="btn-primary">
          Спробувати генератор
        </Link>
        <Link to="/docs" className="btn-secondary">
          Документація API
        </Link>
      </div>
    </div>
  )
}
